import { finite } from "utils/radarDiagnostics";

// The water against the field's soil surface, the number alternate wetting
// and drying (AWD) irrigates by: "+3 cm" is water standing over the soil,
// "-8 cm" water down in it. The node's radar profile keeps how far the soil
// surface stands over the pipe's floor (soil_above_floor_mm) and, from its
// dry floor echo, how far below the radar it is (soil_air_mm: the expected
// floor less that). With them the server adds depth_mm to each analysis
// (positive over the soil, negative under it, inside the pipe) and
// depth_below_tube (the pipe is dry: the water is somewhere below it).

// The server's limits for the soil surface over the pipe's floor.
export const SOIL_MM = [0, 2000];
// The common AWD rule: irrigate once the water is 15 cm under the soil.
export const AWD_IRRIGATE_MM = -150;

// Centimetres, as "4.2 cm" (or "30 cm" when whole and `whole` is set).
const cm = (valueMm, whole = false) => {
  const v = Math.abs(valueMm) / 10;
  return `${whole && Number.isInteger(v) ? v : v.toFixed(1)} cm`;
};

// The server's limits for the pipe's top edge over the soil (a tape measure's height).
export const PIPE_ABOVE_SOIL_MM = [1, 5000];

// The soil reference of a profile (profileFacts()): {aboveFloorMm (how far the pipe's floor lies below the soil: null
// when not known), airMm (the soil surface below the radar), floorMm, basis ("pipe_top": the pipe's measured height
// over the soil less the radar zero; "floor": the expected floor less the soil over it), pipeAboveSoilMm,
// radarZeroMm, radarZeroMeasured}, or null without one.
export function soilReference(facts) {
  const above = finite(facts?.soilAboveFloorMm);
  const floor = finite(facts?.expectedFloorMm);
  const top = finite(facts?.pipeAboveSoilMm);
  const basis = facts?.soilBasis || (top !== null ? "pipe_top" : "floor");
  const air =
    finite(facts?.soilAirMm) ??
    (basis === "floor" && above !== null && floor !== null ? floor - above : null);
  if (air === null) return null;
  if (basis === "floor" && above === null) return null;
  return {
    aboveFloorMm: basis === "pipe_top" ? (floor !== null ? floor - air : null) : above,
    airMm: air,
    floorMm: floor,
    basis,
    pipeAboveSoilMm: top,
    radarZeroMm: finite(facts?.radarZeroMm),
    radarZeroMeasured: !!facts?.radarZeroMeasured,
  };
}

// A reading against the soil surface (depthMm, depthBelowTube as the server
// gives them) in words: {text, kind ("above" | "at" | "below" |
// "below-tube" | "unknown"), irrigate (the common AWD rule says the field
// is dry enough to irrigate)}; null without a soil reference.
export function soilReading({ depthMm = null, depthBelowTube = false }, ref) {
  if (!ref) return null;
  if (depthBelowTube)
    return ref.aboveFloorMm !== null
      ? {
          text: `Tube dry: water more than ${cm(ref.aboveFloorMm, true)} below the soil surface`,
          kind: "below-tube",
          irrigate: ref.aboveFloorMm >= -AWD_IRRIGATE_MM,
        }
      : { text: "Tube dry: water below the bottom of the pipe", kind: "below-tube", irrigate: false };
  const depth = finite(depthMm);
  if (depth === null)
    return {
      text: "Water against the soil surface: not known for this reading",
      kind: "unknown",
      irrigate: false,
    };
  if ((Math.abs(depth) / 10).toFixed(1) === "0.0")
    return { text: "Water: at the soil surface", kind: "at", irrigate: false };
  return depth > 0
    ? {
        text: `Water: ${cm(depth)} above the soil surface`,
        kind: "above",
        irrigate: false,
      }
    : {
        text: `Water: ${cm(depth)} below the soil surface`,
        kind: "below",
        irrigate: depth <= AWD_IRRIGATE_MM,
      };
}

// Where the soil surface is, for the note under the reading: "Soil surface
// 873 mm below the radar, from the pipe's floor (1,173 mm, less 300 mm of
// soil)".
export function soilReferenceText(ref) {
  if (!ref) return "";
  const n = (v) =>
    `${Number(v).toLocaleString("en-SG", { maximumFractionDigits: 1 })} mm`;
  if (ref.basis === "pipe_top" && ref.pipeAboveSoilMm !== null)
    return `Soil surface ${n(ref.airMm)} below the radar, from the top of the pipe (${n(ref.pipeAboveSoilMm)} above the soil, less the radar's ${n(ref.radarZeroMm ?? 20)} inside the top${ref.radarZeroMeasured ? "" : ", assumed"})`;
  return ref.floorMm !== null
    ? `Soil surface ${n(ref.airMm)} below the radar, from the pipe's floor (${n(ref.floorMm)}, less ${n(ref.aboveFloorMm)} of soil)`
    : `Soil surface ${n(ref.airMm)} below the radar`;
}

// The soil surface field as typed (whole millimetres over the pipe's floor,
// blank for none) against the profile's expected floor: {body:
// {soil_above_floor_mm}} or {error}. The server refuses a soil surface at
// or below the radar (not less than the floor, else the pipe's length).
export function soilPayload(
  text,
  { expectedFloorMm = null, pipeLengthMm = null } = {},
) {
  const s = String(text ?? "").trim();
  if (!s) return { body: { soil_above_floor_mm: null } };
  const [lo, hi] = SOIL_MM;
  if (!/^\d+$/.test(s) || Number(s) < lo || Number(s) > hi)
    return {
      error: `Soil surface: whole millimetres from ${lo} to ${hi.toLocaleString("en-SG")} above the pipe's floor, or leave it blank.`,
    };
  const value = Number(s);
  const bottom = finite(expectedFloorMm) ?? finite(pipeLengthMm);
  if (bottom !== null && value >= bottom)
    return {
      error: `Soil surface: less than the ${finite(expectedFloorMm) !== null ? "expected floor" : "pipe length"} (${bottom.toLocaleString("en-SG")} mm), or it would not lie between the radar and the pipe's floor.`,
    };
  return { body: { soil_above_floor_mm: value } };
}

// The pipe's height over the soil as typed (whole millimetres from the soil to the pipe's top edge, blank for none):
// {body: {pipe_above_soil_mm}} or {error}. Less than the pipe's length: the pipe stands in the soil.
export function topPayload(text, { pipeLengthMm = null } = {}) {
  const s = String(text ?? "").trim();
  if (!s) return { body: { pipe_above_soil_mm: null } };
  const [lo, hi] = PIPE_ABOVE_SOIL_MM;
  if (!/^\d+$/.test(s) || Number(s) < lo || Number(s) > hi)
    return {
      error: `Pipe above the soil: whole millimetres from ${lo} to ${hi.toLocaleString("en-SG")}, or leave it blank.`,
    };
  const value = Number(s);
  const pipe = finite(pipeLengthMm);
  if (pipe !== null && value >= pipe)
    return {
      error: `Pipe above the soil: less than the pipe's length (${pipe.toLocaleString("en-SG")} mm), since part of it stands in the soil.`,
    };
  return { body: { pipe_above_soil_mm: value } };
}
